import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Card,
  Button,
  Typography,
  Space,
  Spin,
  Alert,
  Descriptions,
  Row,
  Col,
  Divider,
  Result,
  Form,
  Input,
  Select,
} from 'antd';
import UilLeft from '@iconscout/react-unicons/icons/uil-arrow-left';
import { ManualPaymentService } from '../../services/manualPaymentService';
import { DataService } from '../../config/dataService/smipDataService';
import { PageHeader } from '../page-headers/page-headers';
import { Main } from '../../container/styled';
import { OwnerDetailsBox } from '../../container/borrower/project/projectDetail/Style';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PageRoutes = [
  {
    path: 'index',
    breadcrumbName: 'Dashboard',
  },
  {
    path: 'investments',
    breadcrumbName: 'Investments',
  },
  {
    path: '',
    breadcrumbName: 'Payment',
  },
];

function ManualInvestPaymentPage() {
  const { id: investmentId } = useParams();
  const navigate = useNavigate();
  const { investorId } = useSelector((state) => state.auth);
  const [form] = Form.useForm();

  const [investment, setInvestment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Check if this is a return from payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const returnedInvestmentId = urlParams.get('investmentId');

    // If we have success status and matching investment ID
    if (status === 'success' && returnedInvestmentId === investmentId) {
      setPaymentSuccess(true);
    }
  }, [investmentId]);

  // Fetch investment details
  useEffect(() => {
    const fetchData = async () => {
      if (!investmentId) {
        setError('Investment ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Get investment details using DataService
        const investmentResponse = await DataService.get(`/Investments/${investmentId}?fields=*,*.*`);

        if (!investmentResponse.data) {
          throw new Error('Failed to fetch investment details');
        }

        setInvestment(investmentResponse.data);
      } catch (err) {
        setError(err.message || 'Failed to load investment details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [investmentId]);

  const handleInitiatePayment = () => {
    setShowPaymentForm(true);
  };

  const handleSubmitPayment = async (values) => {
    if (!investment || !investment.project || !investorId) {
      setError('Missing investment, project, or investor information');
      return;
    }

    setProcessingPayment(true);
    setError(null);

    try {
      const paymentData = {
        amount: investment.amount,
        currency: 'USD',
        investorId,
        projectId: investment.project.id,
        investmentId,
        paymentMethod: values.paymentMethod,
        referenceCode: values.referenceCode,
        notes: values.notes,
      };

      const { data, err } = await ManualPaymentService.submitInvestmentPayment(paymentData);

      if (err || !data) {
        throw new Error(err || 'Failed to process manual payment');
      }

      setPaymentSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  const goToDashboard = () => {
    navigate('/investor/investments/portfolio');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <>
        <PageHeader className="ninjadash-page-header-main" title={<h4>Payment Complete</h4>} routes={PageRoutes} />
        <Main style={{ minHeight: 'auto' }}>
          <Result
            status="success"
            title="Payment Successfully Submitted!"
            subTitle="Your manual payment has been recorded. Our team will verify your payment and update your investment status. You can view the details in your investments dashboard."
            extra={[
              <Button type="primary" key="dashboard" onClick={goToDashboard}>
                View My Investments
              </Button>,
              <Button key="back" onClick={goBack}>
                Go Back
              </Button>,
            ]}
          />
        </Main>
      </>
    );
  }

  return (
    <>
      <PageHeader
        className="ninjadash-page-header-main"
        title={<h4>Complete Investment Payment</h4>}
        routes={PageRoutes}
      />
      <Main style={{ minHeight: 'auto' }}>
        <OwnerDetailsBox>
          <Row gutter={15}>
            <Col xs={24}>
              <a
                className="ninjadash-back-btn"
                onClick={(e) => {
                  e.preventDefault();
                  goBack();
                }}
                href="#"
              >
                <UilLeft /> Go back
              </a>
            </Col>

            {error && (
              <Col xs={24} style={{ marginBottom: '20px' }}>
                <Alert message={error} type="error" showIcon />
              </Col>
            )}

            <Col xs={24} lg={16} className="section-margin">
              <Card title="Investment Details" bordered={false}>
                {investment && (
                  <Descriptions column={{ xs: 1, sm: 2 }} layout="vertical">
                    <Descriptions.Item label="Amount">USD {investment.amount?.toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Interest Rate">{investment.interest_rate}%</Descriptions.Item>
                    <Descriptions.Item label="Payment Frequency">{investment.payment_frequency}</Descriptions.Item>
                    <Descriptions.Item label="Status">{investment.status}</Descriptions.Item>
                    <Descriptions.Item label="Date Created">
                      {new Date(investment.date_created).toLocaleDateString()}
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </Card>

              {showPaymentForm && (
                <Card title="Payment Information" style={{ marginTop: '20px' }} bordered={false}>
                  <Form form={form} layout="vertical" onFinish={handleSubmitPayment}>
                    <Form.Item
                      name="paymentMethod"
                      label="Payment Method"
                      rules={[{ required: true, message: 'Please select payment method' }]}
                    >
                      <Select placeholder="Select payment method">
                        <Option value="Bank Transfer">Bank Transfer</Option>
                        <Option value="Check">Check</Option>
                        <Option value="Cash Deposit">Cash Deposit</Option>
                        <Option value="Wire Transfer">Wire Transfer</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="referenceCode"
                      label="Reference/Transaction Code"
                      rules={[{ required: true, message: 'Please enter reference code' }]}
                    >
                      <Input placeholder="Enter bank reference or transaction code" />
                    </Form.Item>

                    <Form.Item name="notes" label="Additional Notes">
                      <TextArea rows={4} placeholder="Enter any additional payment details or notes" />
                    </Form.Item>

                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={processingPayment} block>
                        {processingPayment ? 'Processing...' : 'Submit Payment Details'}
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              )}
            </Col>

            <Col xs={24} lg={8} className="section-margin">
              <Card title="Project Details" bordered={false}>
                {investment && investment.project && (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <Title level={5}>{investment.project.project_title}</Title>
                      <Text type="secondary">{investment.project.location || 'Location not specified'}</Text>
                    </div>

                    <Divider style={{ margin: '12px 0' }} />

                    {!showPaymentForm && (
                      <div>
                        <Button type="primary" size="large" block onClick={handleInitiatePayment}>
                          Make Manual Payment
                        </Button>
                      </div>
                    )}

                    <div style={{ marginTop: '20px' }}>
                      <Title level={5}>Payment Instructions</Title>
                      <Text>
                        <strong>Bank Name:</strong> Global Investment Bank
                        <br />
                        <strong>Account Name:</strong> Investment Platform Holdings
                        <br />
                        <strong>Account Number:</strong> 1234567890
                        <br />
                        <strong>SWIFT/BIC Code:</strong> GLIBUSXX
                        <br />
                        <strong>Reference:</strong> INV-{investmentId}
                      </Text>
                    </div>

                    <Text type="secondary" style={{ fontSize: '12px', textAlign: 'center' }}>
                      By proceeding, you agree to the terms of this investment.
                    </Text>
                  </Space>
                )}
              </Card>
            </Col>
          </Row>
        </OwnerDetailsBox>
      </Main>
    </>
  );
}

export default ManualInvestPaymentPage;
